import { intersection } from 'lodash'
import { hiddenCanvas, pixelRatio, pixelRatioWidth, pixelRatioHeight } from './utils'
import { tick } from './tick'
import * as colors from './colors'
import * as storage from './storage'

export const findById = (id: number): object => {
  return storage.nodes().filter(n => n.id === id)[0]
}

export const findByPosition = (x: number, y: number): array => {
  if (globalThis._CONFIGURATION_ === undefined || globalThis._SIMULATION_ === undefined) return
  tick(hiddenCanvas())
  const hiddenColor: Uint8ClampedArray = hiddenCanvas().getContext('2d').getImageData(x * pixelRatio(), y * pixelRatio(), 1, 1).data
  const colorString: string = `rgb(${hiddenColor[0].toString()}, ${hiddenColor[1].toString()}, ${hiddenColor[2].toString()})`
  return findByHiddenColor(colorString)
}

export const process = (nodes: array): array => {
  const allIds = nodes.map(node => node.id)

  nodes.forEach(node => {
    const nT = nodeType(node)

    node.radius = nT.radius
    node.collisionRadius = nT.collisionRadius
    node.draggable = nT.draggable
    node.highlightable = nT.highlightable
    node.cardable = nT.cardable
    node.core = nT.core
    node.coreDistance = nT.coreDistance
    node.renderWithoutNeighbors = nT.renderWithoutNeighbors
    node.loadNeighborable = nT.loadNeighborable
    node.loadAsNeighbor = nT.loadAsNeighbor
    node.maximumLabelLength = nT.maximumLabelLength
    node.centerable = nT.centerable
    node.drillDownable = nT.drillDownable

    node.style = style(node)
    node.highlighted = highlighted(node)

    if (node.style === 'label') {
      node.label = {
        text: splitName(node, node.maximumLabelLength),
        size: nT.label.size,
        color: nT.label.color
      }

      if (nT.stroke !== undefined) {
        node.stroke = {
          color: nT.stroke.color,
          width: nT.stroke.width
        }
      }
    }

    convertAdjacentNodeIds(node)
    const neighborCount = intersection(node.adjacentNodeIds, allIds).length

    node.pill = {
      text: nT.pill.trackNeighborCount === true && neighborCount >= 1 ? String(neighborCount) : undefined,
      size: nT.pill.size,
      offset: nT.pill.offset,
      color: nT.pill.color,
      background: nT.pill.background
    }

    node.render = node.renderWithoutNeighbors === true || neighborCount >= 1 || node.loadAsNeighbor === true
  })

  return nodes.filter(d => d.render)
}

// As of now, Postgres returns adjacent node IDs as a string, so we are splitting that into an array
export const convertAdjacentNodeIds = (node: object): array => {
  node.adjacentNodeIds = node.adjacentNodeIds.slice(1, -1).split(',')
}

export const visibleColors = (node: object): array => {
  if (globalThis._CONFIGURATION_.visibleColorsByNodeId === undefined) return
  if (node.highlighted === false && anyHighlighted()) return [colors.inactive()]
  return globalThis._CONFIGURATION_.visibleColorsByNodeId[node.id]
}

export const hiddenColor = (node: object): array => {
  return globalThis._CONFIGURATION_.hiddenColorsByNodeId[node.id]
}

export const onCanvas = (node: object): boolean => {
  const x0 = (-globalThis._TRANSFORM_.x / globalThis._TRANSFORM_.k) - node.radius
  const x1 = ((-globalThis._TRANSFORM_.x + pixelRatioWidth()) / globalThis._TRANSFORM_.k) + node.radius // eslint-disable-line
  const y0 = (-globalThis._TRANSFORM_.y / globalThis._TRANSFORM_.k) - node.radius
  const y1 = ((-globalThis._TRANSFORM_.y + pixelRatioHeight()) / globalThis._TRANSFORM_.k) + node.radius // eslint-disable-line
  return node.x >= x0 && node.x <= x1 && node.y >= y0 && node.y <= y1
}

export const image = (node: object): HTMLImageElement => {
  if (globalThis._IMAGES_[node.id] === undefined) {
    const image = new Image()
    if (node.imageUrl === undefined) return
    image.crossOrigin = 'anonymous'
    image.src = node.imageUrl
    globalThis._IMAGES_[node.id] = image
  }
  return globalThis._IMAGES_[node.id]
}

export const pillText = (node: object): string => {
  if (globalThis._NEIGHBOR_PILL_IDS_ === undefined || node.adjacentNodeIds === undefined) return
  if (node.pill.text === undefined) storePillText(node)
  if (node.pill.text.length > 0) return node.pill.text
}

const storePillText = (node: object): void => {
  const relevantIds = intersection(globalThis._NEIGHBOR_PILL_IDS_, node.adjacentNodeIds)
  node.pill.text = relevantIds.length === 1 ? globalThis._NEIGHBOR_PILL_TEXTS_[relevantIds[0]] : ''
}

export const highlighted = (node: object): boolean => {
  if (globalThis._CONFIGURATION_.highlighted === undefined || globalThis._CONFIGURATION_.highlighted === null) return false
  return globalThis._CONFIGURATION_.highlighted.includes(node.id)
}

export const noneHighlighted = (): boolean => {
  return globalThis._CONFIGURATION_.highlighted === null || globalThis._CONFIGURATION_.highlighted === undefined
}

export const anyHighlighted = (): boolean => {
  return !noneHighlighted()
}

export const style = (node: object): string => {
  return nodeType(node).style
}

export const active = (node: object): boolean => {
  return node.highlighted === true || noneHighlighted()
}

export const inactive = (node: object): boolean => {
  return node.highlighted === false && anyHighlighted()
}

const splitName = (node: object, maxLength: number): array => {
  let string = node.name
  if (string === undefined) return []
  const strings = []
  while (string.length > maxLength) {
    let position = string.substring(0, maxLength).lastIndexOf(' ')
    position = position <= 0 ? maxLength : position
    strings.push(string.substring(0, position))
    var i = string.indexOf(' ', position) + 1 // eslint-disable-line
    if (i < position || i > position + maxLength) { i = position } // eslint-disable-line
    string = string.substring(i)
  }
  strings.push(string)
  return strings
}

const findByHiddenColor = (color: string): array => {
  if (globalThis._CONFIGURATION_.nodeIdsByHiddenColor === undefined) return
  return globalThis._CONFIGURATION_.nodeIdsByHiddenColor[color]
}

const nodeType = (node): object => {
  return globalThis._CONFIGURATION_.nodeTypes[node.nodeTypeId]
}
